/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ChannelIdentity } from '@msdyn365-commerce/retail-proxy';
import { buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';

import { getChannelsAction, GetChannelIdentitiesInput } from './get-channels';

/**
 * Channel identity response shape.
 */
interface IChannelIdentity {
    channelIdentities: ChannelIdentity[];
}

/**
 * Input for the B2B-contact-gated channels data action.
 */
export class GetB2bContactChannelsInput implements IActionInput {
    public readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('B2bContactChannels', this.apiSettings);

    public getCacheObjectType = () => 'ChannelIdentities';

    public dataCacheType = (): CacheType => 'request';
}

const createB2bContactChannelsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new GetB2bContactChannelsInput(inputData.requestContext.apiSettings);
};

/**
 * Returns channels only for B2B contacts.
 * Consumers (e.g. the invoices Origin refiner) render this data only for B2B contacts, so this skips the underlying
 * channels request for other (feature-disabled) B2B users to avoid an unnecessary server call on both SSR and CSR.
 * @param input - Data action input.
 * @param context - Data action context.
 * @returns Channel identities, or an empty list when the user is not a B2B contact.
 */
export async function getB2bContactChannels(input: GetB2bContactChannelsInput, context: IActionContext): Promise<IChannelIdentity> {
    if (!context.requestContext.user?.isB2bContact) {
        return { channelIdentities: [] };
    }

    // Reuse the shared channels action to avoid duplicating the retail-proxy call. It only needs the api settings,
    // which we forward via a real GetChannelIdentitiesInput so no cast is required.
    return getChannelsAction(new GetChannelIdentitiesInput(input.apiSettings), context);
}

export const getB2bContactChannelsDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/order-management/get-b2b-contact-channels',
    action: <IAction<IChannelIdentity>>getB2bContactChannels,
    input: createB2bContactChannelsInput
});

export default getB2bContactChannelsDataAction;
